import { default as axios } from 'axios'
import UnauthorizedError from './api/error/UnauthorizedError'
import ValidationError from './api/error/ValidationError'
import SystemError from './api/error/SystemError'
import ApplicationError from './api/error/ApplicationError'
import InternalError from './api/error/InternalError'
import NoResponseError from './api/error/NoResponseError'
import { EventEmitter } from 'events'
import qs from 'qs'

axios.interceptors.request.use((config) => {
  config.paramsSerializer = (params) => {
    return qs.stringify(params, {
      arrayFormat: 'brackets',
      encode: false,
    })
  }
  return config
})

let client = axios.create({
  timeout: 30000,
  headers: {
    //'Accept-Language': process.env.VUE_APP_I18N_LOCALE,
    'Content-Type': 'application/json',
    //'x-api-key': '04af45862027092a760938171d8d05201b68010ab4a483d2b2d79907a17a66d3'
  },
})

const EVENT = {
  ERROR: 'error',
}

const eventEmitter = new EventEmitter()

function blobDataReader(data) {
  const fileReader = new FileReader()

  return new Promise((resolve, reject) => {
    fileReader.onerror = () => {
      fileReader.abort()
      reject(new Error('Error reading file'))
    }

    fileReader.onload = () => {
      resolve(fileReader.result)
    }

    fileReader.readAsText(data)
  })
}

async function handleError(error) {
  if (error.response) {
    let data = error.response.data

    if (data instanceof ArrayBuffer) {
      data = new Blob([data])
    }

    if (data instanceof Blob) {
      data = await blobDataReader(data)
      data = JSON.parse(data)
    }

    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    switch (error.response.status) {
      case 401:
        return new UnauthorizedError()
      case 400:
        if (data.code) {
          switch (data.code) {
            case 'VALIDATION_ERROR':
              return new ValidationError(data.errors)

            default:
              return new ApplicationError(
                data.code,
                data.message,
                data.errors || null,
              )
          }
        }
        break
      case 500:
        return new SystemError()
    }
  } else if (error.request) {
    // The request was made but no response was received
    // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
    // http.ClientRequest in node.js
    return new NoResponseError()
  }

  return new InternalError(error)
}

/**
 *
 * @param {} filterVal
 * @return {boolean}
 */
function isNotEmptyFilterValue(filterVal) {
  if (filterVal === null) {
    return false
  }

  switch (typeof filterVal) {
    case 'object': {
      let results = []
      for (let [key, value] of Object.entries(filterVal)) {
        results.push(value !== null)
      }

      return results.every((val) => val)
    }
  }

  return true
}

async function makeRequest(type, ...args) {
  if (!client.defaults.baseURL) {
    throw new Error('Client is not initialized')
  }

  try {
    return await client[type](...args)
  } catch (e) {
    let error = await handleError(e)

    eventEmitter.emit(EVENT.ERROR, error)
    throw error
  }
}

const apiClient = {
  event: eventEmitter,

  /**
   *
   * @param baseUrl
   */
  init: function (baseUrl) {
    client.defaults.baseURL = baseUrl
  },
  /**
   *
   * @param {string} url
   * @param {Object|null} params
   * @return {string}
   */
  url: function (url, params = null) {
    if (params === null) {
      return url
    }

    for (let [name, value] of Object.entries(params)) {
      url = url.replace(`{${name}}`, value)
    }

    return url
  },
  /**
   * @param {string} url
   * @param {Object|null} config
   * @return Promise<AxiosResponse>
   */
  get: async function (url, params = null, config = null) {
    if (params) {
      //let uriParams = decodeURIComponent(new URLSearchParams(Object.entries(params)).toString());

      url = url + '?' + qs.stringify(params)
    }

    let defaultConfig = {
      headers: {
        'Content-Type': 'application/json',
      },
      data: {},
    }

    config = config ? { ...defaultConfig, ...config } : defaultConfig

    return await makeRequest('get', url, config)
  },
  /**
   * @param {string} url
   * @param {Object|null} config
   * @return Promise<AxiosResponse>
   */
  getWithFilterParams: async function (
    url,
    filterParams = null,
    config = null,
  ) {
    if (filterParams) {
      filterParams = Object.fromEntries(
        Object.entries(filterParams).filter(([key, value]) =>
          isNotEmptyFilterValue(value),
        ),
      )

      url = url + '?' + qs.stringify(filterParams)
    }

    return await this.get(url, null, config)
  },
  /**
   *
   * @param {string} url
   * @param {Object|null} data
   * @param {Object|null}config
   * @return {Promise<AxiosResponse>}
   */
  post: async function (url, data = null, config = null) {
    return await makeRequest('post', url, data, config)
  },
  /**
   *
   * @param {string} url
   * @param {Object|null} data
   * @param {Object|null}config
   * @return {Promise<AxiosResponse>}
   */
  put: async function (url, data = null, config = null) {
    return await makeRequest('put', url, data, config)
  },
  /**
   *
   * @param {string} url
   * @param {Object|null} data
   * @param {Object|null}config
   * @return {Promise<AxiosResponse>}
   */
  patch: async function (url, data = null, config = null) {
    return await makeRequest('patch', url, data, config)
  },
  /**
   *
   * @param {string} url
   * @param {Object|null}config
   * @return {Promise<AxiosResponse>}
   */
  delete: async function (url, config = null) {
    return await makeRequest('delete', url, config)
  },
  download: async function (url, params, config = {}) {
    config.responseType = 'arraybuffer'

    let response = await this.get(url, params, config)

    let filename = null
    let disposition = response.headers['content-disposition']

    if (disposition && disposition.indexOf('attachment') !== -1) {
      let filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
      let matches = filenameRegex.exec(disposition)
      if (matches != null && matches[1])
        filename = matches[1].replace(/['"]/g, '')
    }

    let blob = new Blob([response.data], {
      type: response.headers['content-type'],
    })
    let link = document.createElement('a')
    link.href = window.URL.createObjectURL(blob)
    link.download = filename
    link.click()

    return response
  },
  /**
   *
   * @param {string} url
   * @param {FormData} data
   * @param {Object|null}config
   * @return {Promise<AxiosResponse>}
   */
  upload: async function (url, formData = null, config = {}) {
    return await makeRequest('post', url, formData, config)
  },
  setAuthenticationToken: function (token) {
    client.defaults.headers.common['Authorization'] = 'Bearer ' + token
  },
  deleteAuthenticationToken: function () {
    client.defaults.headers.common['Authorization'] = ''
  },
}

export default apiClient
export { EVENT }
